import React, { useCallback, useEffect, useState } from "react";
import { Box, Button, Stack } from "@mui/material";

import { Connect } from "./Connect";
import { Content } from "./Content";
import { CrunchbaseData } from "store/types";
import { IApiServiceError, useDealActions, useDealIdSelector } from "store";
import { useModals } from "hooks";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import { DealRoleGuard } from "components/guards";
import { RoleName } from "../../../constants";

interface Props {
  onClose: () => void;
  crunchbaseId: number | null;
}

export const Main: React.FC<Props> = ({ onClose, crunchbaseId }) => {
  const dealId = useDealIdSelector();

  const { getCrucnbaseAction, createCrucnbaseAction } = useDealActions();
  const { errorModal } = useModals();

  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<CrunchbaseData | null>(null);
  const [connectLink, setConnectLink] = useState<string>("");
  const [connectError, setConnectError] = useState<string>("");

  const getCrunchbase = useCallback(async (id: number) => {
    try {
      setLoading(true);
      const response = await getCrucnbaseAction(id);
      setData(response);
    } catch (error) {
      errorModal({ msg: (error as IApiServiceError).message, action: onClose });
    } finally {
      setLoading(false);
    }
  }, []);

  const createCrucnbase = useCallback(async () => {
    try {
      if (!dealId) return;
      setLoading(true);
      if (connectError) setConnectError("");
      const response = await createCrucnbaseAction({ dealId, crunchbase_link: connectLink });
      setData(response);
    } catch (error) {
      setConnectError((error as IApiServiceError).message);
    } finally {
      setLoading(false);
    }
  }, [connectLink, dealId, connectError]);

  useEffect(() => {
    if (!crunchbaseId) return;
    if (!dealId) return;
    getCrunchbase(dealId);
  }, []);

  return (
    <AsyncContentMui isLoading={loading} spinnerProps={{ size: 100 }}>
      <Box flexGrow="1" sx={{ overflowY: "auto" }} padding="30px 30px 40px 30px">
        {data ? (
          <Content reconnect={() => setData(null)} data={data} />
        ) : (
          <Connect inputValue={connectLink} setInputValue={setConnectLink} errorMessage={connectError} />
        )}
      </Box>
      <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          gap="21px"
          padding="29px"
          boxShadow="0 -1px 5px 1px rgba(0, 0, 0, 0.08)"
        >
          <Button onClick={onClose} variant="outlined">
            {data ? "Close" : "Cancel"}
          </Button>
          {!data && (
            <Button disabled={loading || !connectLink} onClick={createCrucnbase} variant="contained">
              Connect
            </Button>
          )}
        </Stack>
      </DealRoleGuard>
    </AsyncContentMui>
  );
};
